import { forwardRef } from 'react'
import { CustomContentProps } from 'notistack'
import { closeSnackbar } from 'notistack'
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material'
import { Close as CloseIcon } from '@mui/icons-material'
import { Alert, IconButton, Paper } from '@mui/material'
import { Button } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { styled } from '@mui/material/styles'

import { AdditionalAction } from '@helpers'
import { SnackType } from '@types'

interface NotistackSnackbarProps extends CustomContentProps {
  additionalAction?: AdditionalAction
  showCancel?: boolean
  details?: string
  alertSx?: SxProps<Theme>
}

const StyledAlert = styled(Alert)(() => ({
  maxWidth: 600,
  minWidth: 300,
  maxHeight: 100,
  '& .MuiAlert-message': {
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
}))

export const NotistackSnackbar = forwardRef<HTMLDivElement, NotistackSnackbarProps>(
  (props, ref) => {
    const {
      id,
      statusCode,
      variant,
      message,
      requestArgs,
      additionalAction,
      showCancel,
      details,
      alertSx = {},
    } = props

    const status = statusCode ? `${statusCode}: ` : ''
    const formattedMessage = `${status}${message}`

    const formatObject = (value: unknown) => {
      if (!value) {
        return value
      }

      return JSON.stringify(value, null, 2)
    }

    const handleCopy = () => {
      const textLines = [
        `message: ${message}`,
        `details: ${details}`,
        `statusCode: ${statusCode}`,
        `url: ${requestArgs?.url}`,
        `method: ${requestArgs?.method}`,
        `body: ${formatObject(requestArgs?.body ?? {})}`,
        `params: ${formatObject(requestArgs?.params ?? {})}`,
      ]

      const text = textLines.join('\n')

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
      }
    }

    return (
      <div ref={ref}>
        <Paper elevation={3}>
          <StyledAlert
            severity={variant as SnackType}
            action={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                {additionalAction && (
                  <Button
                    color='inherit'
                    size='small'
                    variant='outlined'
                    sx={{
                      fontWeight: 'bold',
                      py: 0.5,
                      borderColor: 'currentColor',
                      '&:hover': {
                        borderColor: 'currentColor',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                    onClick={() => {
                      additionalAction.onClick()
                      closeSnackbar(id)
                    }}
                  >
                    {additionalAction.label}
                  </Button>
                )}

                {requestArgs && (
                  <IconButton color='inherit' size='small' onClick={handleCopy}>
                    <ContentCopyIcon fontSize='small' />
                  </IconButton>
                )}

                {showCancel && (
                  <IconButton
                    color='inherit'
                    size='small'
                    sx={{ p: 0.5 }}
                    onClick={() => closeSnackbar(id)}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                )}
              </div>
            }
            sx={{
              '& .MuiAlert-action': {
                padding: '0 8px',
                alignItems: 'center',
                display: 'flex',
              },
              ...alertSx,
            }}
          >
            {formattedMessage}
          </StyledAlert>
        </Paper>
      </div>
    )
  }
)

NotistackSnackbar.displayName = 'NotistackSnackbar'
